import { useTheme } from '@/contexts/ThemeContext';

export default function Hero() {
  const { isDark } = useTheme();

  const handleScroll = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      className={`relative min-h-screen flex items-center overflow-hidden transition-colors duration-500 ${
        isDark ? 'bg-[#060b18]' : 'bg-slate-50'
      }`}
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=dark%20futuristic%20technology%20abstract%20background%20with%20deep%20navy%20blue%20and%20black%20tones%2C%20subtle%20glowing%20grid%20lines%2C%20circuit%20board%20patterns%2C%20soft%20cyan%20light%20accents%2C%20ultra%20high%20resolution%2C%20professional%20corporate%20tech%20aesthetic%2C%20no%20people%2C%20clean%20minimal%20dark%20background%20for%20software%20company&width=1920&height=1080&seq=hero-bg-granato-01&orientation=landscape"
          alt="Background tecnológico"
          className={`w-full h-full object-cover object-center transition-opacity duration-500 ${isDark ? 'opacity-30' : 'opacity-8'}`}
        />
        {isDark ? (
          <>
            <div className="absolute inset-0 bg-gradient-to-br from-[#060b18] via-[#0a1628]/80 to-[#060b18]"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-[#060b18] via-transparent to-transparent"></div>
          </>
        ) : (
          <>
            <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white/90 to-slate-50"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-slate-50 via-transparent to-transparent"></div>
          </>
        )}
      </div>

      {/* Glow effects */}
      <div className={`absolute top-1/3 left-1/4 w-96 h-96 rounded-full blur-3xl pointer-events-none ${isDark ? 'bg-cyan-500/10' : 'bg-cyan-400/15'}`}></div>
      <div className={`absolute bottom-1/3 right-1/4 w-80 h-80 rounded-full blur-3xl pointer-events-none ${isDark ? 'bg-blue-600/10' : 'bg-cyan-300/10'}`}></div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-20">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Text */}
          <div className="flex flex-col gap-8">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border w-fit ${
              isDark ? 'border-cyan-500/30 bg-cyan-500/10' : 'border-cyan-400/40 bg-cyan-50'
            }`}>
              <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></span>
              <span className="text-cyan-600 text-xs font-semibold tracking-widest uppercase">Software Sob Medida · B2B · ERP</span>
            </div>

            <h1 className={`text-4xl lg:text-6xl font-bold leading-tight tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Sistemas que elevam{' '}
              <span className="bg-gradient-to-r from-cyan-500 to-cyan-400 bg-clip-text text-transparent">
                controle, produtividade
              </span>{' '}
              e inteligência do negócio
            </h1>

            <p className={`text-lg leading-relaxed max-w-xl ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              Desenvolvemos sistemas web, aplicativos mobile e ERPs especializados para escolas, empresas de reciclagem e corporações. Soluções robustas, personalizadas e focadas em resultados reais.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => handleScroll('#contato')}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg text-base font-semibold text-white whitespace-nowrap cursor-pointer transition-all duration-200 hover:scale-105"
                style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)' }}
              >
                <i className="ri-rocket-line"></i>
                Solicitar Projeto
              </button>
              <button
                onClick={() => handleScroll('#solucoes')}
                className={`inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg text-base font-semibold whitespace-nowrap cursor-pointer transition-all duration-200 ${
                  isDark
                    ? 'text-white border border-white/20 bg-white/5 hover:bg-white/10'
                    : 'text-slate-700 border border-slate-300 bg-white hover:bg-slate-50'
                }`}
              >
                <i className="ri-apps-2-line"></i>
                Ver Soluções
              </button>
            </div>

            {/* Stats */}
            <div className={`flex flex-wrap gap-8 pt-4 border-t ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
              {[
                { value: '120+', label: 'Projetos Entregues' },
                { value: '98%', label: 'Satisfação dos Clientes' },
                { value: '8+', label: 'Anos de Mercado' },
                { value: '40+', label: 'Empresas Atendidas' },
              ].map((stat) => (
                <div key={stat.label} className="flex flex-col">
                  <span className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{stat.value}</span>
                  <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>{stat.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Dashboard Mockup */}
          <div className="relative hidden lg:flex items-center justify-center">
            <div className="relative w-full max-w-lg">
              {/* Main dashboard card */}
              <div
                className={`rounded-2xl overflow-hidden border shadow-2xl ${
                  isDark ? 'border-white/10 shadow-black/60' : 'border-slate-200 shadow-slate-300/40'
                }`}
                style={{ background: isDark ? 'linear-gradient(135deg, rgba(15,23,42,0.95) 0%, rgba(10,16,30,0.98) 100%)' : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)' }}
              >
                {/* Window bar */}
                <div className={`flex items-center gap-2 px-4 py-3 border-b ${isDark ? 'border-white/10 bg-white/5' : 'border-slate-100 bg-slate-50'}`}>
                  <div className="w-3 h-3 rounded-full bg-red-500/70"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500/70"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500/70"></div>
                  <span className={`ml-3 text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Granato ERP — Painel Administrativo</span>
                </div>
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20ERP%20dashboard%20interface%20screenshot%20with%20dark%20navy%20blue%20theme%2C%20data%20charts%2C%20KPI%20cards%2C%20financial%20graphs%2C%20tables%20with%20data%2C%20sidebar%20navigation%2C%20modern%20UI%20design%2C%20clean%20corporate%20software%20interface%2C%20cyan%20accent%20colors%2C%20professional%20business%20intelligence%20dashboard&width=600&height=380&seq=hero-dashboard-01&orientation=landscape"
                  alt="Dashboard ERP Granato"
                  className="w-full object-cover object-top"
                />
              </div>

              {/* Floating card 1 */}
              <div
                className={`absolute -bottom-6 -left-8 rounded-xl p-4 border shadow-xl backdrop-blur-md ${
                  isDark ? 'border-white/10' : 'border-cyan-200/60'
                }`}
                style={{ background: isDark ? 'rgba(6,182,212,0.12)' : 'rgba(236,254,255,0.95)' }}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${isDark ? 'bg-cyan-500/20' : 'bg-cyan-100'}`}>
                    <i className="ri-bar-chart-2-line text-cyan-500 text-xl"></i>
                  </div>
                  <div>
                    <div className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>Receita Mensal</div>
                    <div className="text-cyan-500 text-xs">+24% este mês</div>
                  </div>
                </div>
              </div>

              {/* Floating card 2 */}
              <div
                className={`absolute -top-6 -right-6 rounded-xl p-4 border shadow-xl backdrop-blur-md ${
                  isDark ? 'border-white/10' : 'border-slate-200'
                }`}
                style={{ background: isDark ? 'rgba(15,23,42,0.9)' : 'rgba(255,255,255,0.97)' }}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${isDark ? 'bg-green-500/20' : 'bg-green-100'}`}>
                    <i className="ri-shield-check-line text-green-500 text-xl"></i>
                  </div>
                  <div>
                    <div className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>Sistema Online</div>
                    <div className="text-green-500 text-xs">99.9% uptime</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className={`text-xs tracking-widest uppercase ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>Scroll</span>
        <i className={`ri-arrow-down-line ${isDark ? 'text-slate-600' : 'text-slate-400'}`}></i>
      </div>
    </section>
  );
}
